import { ThunkAction, UnknownAction } from '@reduxjs/toolkit';
import { modules } from '@veritone/glc-redux';

import callGraphQLApi from '@helpers/callGraphQLApi';
import { resolveRenamedRegistryId } from '@helpers/registryConfig';

import * as Actions from './actions';
import { NotificationInterface, NotificationSDOId } from './models';
import { SCHEMA_ID_NOT_FOUND, NOOP_JOB_ACTION } from './actions';
import { get } from 'lodash';
import { AsyncOrSync } from 'ts-essentials';
export type Thunk<P, R = any> = (
  payload: P,
  ...args: any[]
) => ThunkAction<Promise<R | undefined>, any, unknown, UnknownAction>;

const {
  config: { getConfig },
} = modules;

export interface GetLatestSchemaIdResponse {
  dataRegistry: { publishedSchema: { id: string } };
}

/**
 * Resolved schema ids, keyed by `${scope}-${dataRegistryId}`.
 *
 * Deliberately in-memory rather than `sessionStorage`: sessionStorage survives
 * a page reload, so once a data registry published a new schema version the tab
 * went on resolving the superseded id until it was *closed* — reloading, the
 * one thing everybody reaches for, did nothing, and writes kept failing schema
 * validation. The cache only exists to spare a registry round trip per thunk
 * call, which a module-level map does just as well while making a reload a real
 * reset.
 *
 * Entries do not expire. A registry only publishes a new schema version when a
 * schema change is actually needed, which is rare and irregular — so a TTL would
 * mean every tab re-resolving ~10 registries on a timer forever to catch an
 * event that almost never fires.
 *
 * Staleness is handled by signal instead — but be precise about how much of that
 * is actually wired. `clearAllCachedSchemaIds` on logout and on boot covers every
 * registry. `clearCachedSchemaId` on a schema validation error is the exact,
 * immediate tell that an id has been superseded, and today **only the
 * redaction-statuses registry wires it** (see `clearRedactionStatusSchemaId`).
 * For every other registry a mid-session republish means writes fail until the
 * page is reloaded; reload is the recovery, not one of three. Generalising that
 * signal is the subject of `plans/plan-lazy-schema-id-recovery.md`.
 *
 * Main thread only. Nothing bridges invalidation across the worker boundary, so
 * a worker-side copy of this module would hold a second, independently stale
 * cache — if the worker store ever needs a schema id, invalidation has to
 * become an action over `src/utils/workeredux` first.
 */
const schemaIdCache = new Map<string, string>();

/**
 * Lookups currently in flight, so the several thunks that fire on a page load
 * against the same registry share one query instead of racing N of them.
 */
const schemaIdRequests = new Map<string, Promise<string | undefined>>();

/**
 * Per-key invalidation counter. A lookup snapshots its key's value before
 * querying and refuses to populate the cache if it moved while the query was in
 * flight — otherwise a request issued *before* a republish can land *after* the
 * validation-error retry that cleared the cache, reinstating the superseded id.
 * With no TTL to age it back out, that entry would then stand for the rest of
 * the session.
 *
 * Keyed rather than global: ~10 registries resolve concurrently at boot, and a
 * single shared counter meant invalidating one of them made all the others
 * discard results that were never in question.
 */
const schemaIdGenerations = new Map<string, number>();

const getSchemaIdCacheKey = (dataRegistryId: string, scope?: string) =>
  `${scope || ''}-${dataRegistryId}`;

const bumpGeneration = (cacheKey: string) =>
  schemaIdGenerations.set(
    cacheKey,
    (schemaIdGenerations.get(cacheKey) ?? 0) + 1
  );

/**
 * Forget one registry's resolved id, so the next lookup re-resolves. Callers
 * use this when a write comes back with a schema validation error — the signal
 * that the cached id has been superseded.
 *
 * Prefer a wrapper that sits next to the matching `lookupLatest*SchemaId`, so
 * the registry id and scope are resolved from config exactly once (see
 * `clearRedactionStatusSchemaId`). Rebuilding those arguments at a call site
 * duplicates the key's inputs with nothing to keep the two in step.
 */
export const clearCachedSchemaId = (dataRegistryId: string, scope?: string) => {
  const cacheKey = getSchemaIdCacheKey(dataRegistryId, scope);
  bumpGeneration(cacheKey);
  schemaIdCache.delete(cacheKey);
  schemaIdRequests.delete(cacheKey);
};

/**
 * Drops every registry's resolved id. Wired to logout and to boot; also used by
 * tests. Bumps every key that could currently be in flight, so a lookup that
 * started before the clear cannot write its result back afterwards.
 */
export const clearAllCachedSchemaIds = () => {
  for (const cacheKey of new Set([
    ...schemaIdCache.keys(),
    ...schemaIdRequests.keys(),
    ...schemaIdGenerations.keys(),
  ])) {
    bumpGeneration(cacheKey);
  }
  schemaIdCache.clear();
  schemaIdRequests.clear();
};

/**
 * Never rejects: a failed lookup resolves to undefined so the shared in-flight
 * promise can be awaited by every joining caller, each of which then dispatches
 * its own SCHEMA_ID_NOT_FOUND against its own dispatch.
 */
const resolveSchemaIdFromRegistry = async (
  dispatch: (a: UnknownAction) => AsyncOrSync<void>,
  getState: () => any,
  dataRegistryId: string,
  registryName: string | undefined,
  cacheKey: string
): Promise<string | undefined> => {
  const generation = schemaIdGenerations.get(cacheKey) ?? 0;
  try {
    const resp = await callGraphQLApi<GetLatestSchemaIdResponse>({
      actionTypes: [NOOP_JOB_ACTION, NOOP_JOB_ACTION, NOOP_JOB_ACTION],
      query: `
          query fetchLatestSchema($dataRegistryId: ID!) {
            dataRegistry(id: $dataRegistryId) {
              id
              name
              publishedSchema {
                id
              }
            }
          }`,
      variables: { dataRegistryId },
      dispatch,
      getState,
    });

    const schemaId: string | undefined = get(
      resp,
      'dataRegistry.publishedSchema.id'
    );
    // Still return the id to this caller — it is what the registry says right
    // now. Only the shared cache entry is withheld, so the next caller resolves
    // afresh rather than inheriting a result that raced an invalidation.
    if (schemaId && generation === (schemaIdGenerations.get(cacheKey) ?? 0)) {
      schemaIdCache.set(cacheKey, schemaId);
    }
    return schemaId;
  } catch (err) {
    // The only record of this failure: callers see an undefined id and a
    // generic SCHEMA_ID_NOT_FOUND, which cannot distinguish a 5xx from an
    // expired token from a registry with no published schema.
    console.error('getLatestSchemaId failed', {
      registryName,
      dataRegistryId,
      cacheKey,
      err,
    });
    return undefined;
  }
};

export const getLatestSchemaId = async (
  dispatch: (a: UnknownAction) => AsyncOrSync<void>,
  getState: () => any,
  dataRegistryId?: string,
  registryName?: string,
  scope?: string
) => {
  if (!dataRegistryId) {
    dispatch(SCHEMA_ID_NOT_FOUND({ registryName }));
    return;
  }
  const cacheKey = getSchemaIdCacheKey(dataRegistryId, scope);

  const cached = schemaIdCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const lookup = () =>
    resolveSchemaIdFromRegistry(
      dispatch,
      getState,
      dataRegistryId,
      registryName,
      cacheKey
    );

  let schemaId: string | undefined;
  const inFlight = schemaIdRequests.get(cacheKey);
  if (inFlight) {
    schemaId = await inFlight;
    // Successes are shared; failures are not. `callGraphQLApi` reports an
    // expired token by dispatching logout() through the dispatch it was given,
    // and the in-flight request carries whichever caller happened to seed it —
    // `fetchAllTDOsForExport` passes lodash `noop`, which drops it. Retrying on
    // our own dispatch restores the pre-dedupe guarantee that every caller sees
    // its own auth failure, and costs an extra request only on the path that
    // was already failing.
    if (!schemaId) {
      schemaId = await lookup();
    }
  } else {
    const request = lookup();
    schemaIdRequests.set(cacheKey, request);
    const forget = () => {
      // Only drop our own entry: a clearCachedSchemaId() during the request
      // may already have replaced it with a newer lookup.
      if (schemaIdRequests.get(cacheKey) === request) {
        schemaIdRequests.delete(cacheKey);
      }
    };
    void request.then(forget, forget);
    schemaId = await request;
  }

  if (!schemaId) {
    dispatch(SCHEMA_ID_NOT_FOUND({ registryName }));
  }
  return schemaId;
};

/**
 * Upsert an SDO. Leave id undefined for insert.
 */
export interface UpsertJobSDOResponse {
  createStructuredData: {
    id: NotificationSDOId;
    data: NotificationInterface;
    createdDateTime: string;
    modifiedDateTime: string;
  };
}
export const upsertJobSDO: Thunk<{
  readonly id?: string;
  readonly data: NotificationInterface;
}> =
  ({ id, data }) =>
  async (dispatch, getState) => {
    const schemaId = await lookupLatestSchemaId(dispatch, getState);
    if (!schemaId) {
      return;
    }
    const query = `
      mutation upsertSDOs($id: ID, $schemaId: ID!, $data: JSONData!) {
        createStructuredData(input: {
          id: $id,
          schemaId: $schemaId,
          data: $data
        }) {
          id
          data
          createdDateTime
          modifiedDateTime
        }
      }
    `;
    return await callGraphQLApi<UpsertJobSDOResponse>({
      actionTypes: [
        Actions.NOOP_JOB_ACTION,
        Actions.UPSERT_JOB_SDO_SUCCESS,
        Actions.UPSERT_JOB_SDO_FAILURE,
      ],
      query,
      variables: { schemaId, id, data },
      dispatch,
      getState,
    });
  };

export type UpdateMultyJobSDOResponse = Record<
  string,
  {
    id: string;
    data: any;
    createdDateTime: string;
    modifiedDateTime: string;
  }
>;
export const updateMultyJobSDO: Thunk<{
  readonly sdos: ReadonlyArray<{
    sdoId: NotificationSDOId;
    data: NotificationInterface;
  }>;
}> =
  ({ sdos }) =>
  async (dispatch, getState) => {
    const schemaId = await lookupLatestSchemaId(dispatch, getState);
    if (!schemaId) {
      return;
    }
    const query = `
    mutation updateSDOs {
      ${sdos
        .map(
          (sdo, index) => `sdoId${index}: createStructuredData(input: {
            id: "${sdo.sdoId}",
            schemaId: "${schemaId}",
            data: {
              status: "${sdo.data.status}",
              jobId: "${sdo.data.jobId}",
              tdoId: "${sdo.data.tdoId}",
              userId: "${sdo.data.userId}",
              tdoName: "${sdo.data.tdoName}",
              engineName: "${sdo.data.engineName}",
              deleted: ${sdo.data.deleted},
            }
          }
          ) {
            id
            data
            createdDateTime
            modifiedDateTime
          }`
        )
        .join(',')}
    }
  `;
    return await callGraphQLApi<UpdateMultyJobSDOResponse>({
      actionTypes: [
        Actions.UPDATE_MULTY_JOB_SDO,
        Actions.UPDATE_MULTY_JOB_SDO_SUCCESS,
        Actions.UPDATE_MULTY_JOB_SDO_FAILURE,
      ],
      query,
      variables: {},
      dispatch,
      getState,
    });
  };

// /**
//  * Delete an SDO by ID.
//  * @param request - SDO Id and TDO Id required.
//  */
export interface DeleteJobSDOResponse {
  deleteStructuredData: {
    id: string;
  };
}
export const deleteJobSDO: Thunk<{
  readonly id: string;
}> =
  ({ id }) =>
  async (dispatch, getState) => {
    const schemaId = await lookupLatestSchemaId(dispatch, getState);
    if (!schemaId) {
      return;
    }
    const query = `
      mutation deleteSDO($id: ID!, $schemaId: ID!) {
        deleteStructuredData(input: {
          id: $id,
          schemaId: $schemaId,
        }) {
          id
        }
      }
    `;

    return await callGraphQLApi<DeleteJobSDOResponse>({
      actionTypes: [
        Actions.NOOP_JOB_ACTION,
        Actions.DELETE_JOB_SDO_SUCCESS,
        Actions.DELETE_JOB_SDO_FAILURE,
      ],
      query,
      variables: { schemaId, id },
      dispatch,
      getState,
    });
  };

export type DeleteMultiJobSDOResponse = Record<
  string,
  {
    id: string;
  }
>;
export const deleteMultiJobSDO: Thunk<{
  readonly ids: ReadonlyArray<string>;
}> =
  ({ ids }) =>
  async (dispatch, getState) => {
    const schemaId = await lookupLatestSchemaId(dispatch, getState);
    if (!schemaId) {
      return;
    }
    const query = `
      mutation deleteSDOs {
        ${ids
          .map(
            (
              id,
              index
            ) => `sdoId${index}: deleteStructuredData(input: {id: "${id}", schemaId: "${schemaId}"}) {
              id
            }`
          )
          .join(',')}
      }
    `;

    return await callGraphQLApi<DeleteMultiJobSDOResponse>({
      actionTypes: [
        Actions.DELETE_MULTY_JOB_SDO,
        Actions.DELETE_MULTY_JOB_SDO_SUCCESS,
        Actions.DELETE_MULTY_JOB_SDO_FAILURE,
      ],
      query,
      variables: {},
      dispatch,
      getState,
    });
  };

/**
 * Fetch a list of SDOs.
 */
export interface FetchJobSDOsResponse {
  structuredDataObjects: {
    records: {
      id: string;
      data: any;
      createdDateTime: string;
      modifiedDateTime: string;
    }[];
  };
}
export const fetchJobSDOs: Thunk<{
  readonly limit: number;
  readonly offset: number;
  readonly userId: string;
}> =
  ({ limit, offset, userId }) =>
  async (dispatch, getState) => {
    const schemaId = await lookupLatestSchemaId(dispatch, getState);
    if (!schemaId) {
      return;
    }
    const query = `
      query listSDOs($schemaId: ID!, $limit: Int!, $offset: Int, $filter: JSONData) {
        structuredDataObjects(
          schemaId: $schemaId,
          limit: $limit,
          offset: $offset,
          orderBy: [{field: modifiedDateTime, direction: desc}],
          filter: $filter
        ) {
          records {
            id
            data
            createdDateTime
            modifiedDateTime
          }
        }
      }
    `;

    return await callGraphQLApi<FetchJobSDOsResponse>({
      actionTypes: [
        Actions.NOOP_JOB_ACTION,
        Actions.FETCH_JOB_SDOS_SUCCESS,
        Actions.FETCH_JOB_SDOS_FAILURE,
      ],
      query,
      variables: { schemaId, limit, offset, filter: { userId } },
      dispatch,
      getState,
    });
  };

// fetch status of jobs in sdo
export type FetchEachJobResponse = Record<
  string,
  {
    id: string;
    status: string;
  }
>;
export const fetchEachJob: Thunk<{
  sdos: ReadonlyArray<NotificationInterface>;
}> =
  ({ sdos }) =>
  async (dispatch, getState) => {
    const query = `
    query mapJobs {
      ${sdos
        .map(
          ({ jobId }) => `jobId${jobId}: job(id: "${jobId}") {
        id
        status
      }`
        )
        .join(',')}
    }
    `;

    return await callGraphQLApi<FetchEachJobResponse>({
      actionTypes: [
        Actions.NOOP_JOB_ACTION,
        Actions.FETCH_JOB_STATUS_SUCCESS,
        Actions.FETCH_JOB_STATUS_FAILURE,
      ],
      query,
      variables: {},
      dispatch,
      getState,
    });
  };

// /**
//  * Use DataRegistry to lookup the latest published SDO Schema ID.
//  * @returns {string} Schema Id
//  */
const lookupLatestSchemaId = (
  () => async (dispatch: (a: UnknownAction) => void, getState: () => any) => {
    const {
      redactNotificationRegistryId,
      // The one place permitted to read the deprecated key — everywhere else the
      // lint rule should keep flagging it.
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      redactNotificationSchemaId,
      apiRoot,
    } = getConfig<Window['config']>(getState());

    // `redactNotificationSchemaId` is a data registry id despite its name; the
    // correctly named key wins while both are accepted. See @helpers/registryConfig.
    const dataRegistryId = resolveRenamedRegistryId({
      registryId: redactNotificationRegistryId,
      legacyRegistryId: redactNotificationSchemaId,
      registryKey: 'redactNotificationRegistryId',
      legacyKey: 'redactNotificationSchemaId',
    });

    return await getLatestSchemaId(
      dispatch,
      getState,
      dataRegistryId,
      'redactNotificationRegistryId',
      apiRoot
    );
  }
)();

export interface RetryJobResponse {
  retryJob: {
    id: string;
    status: string;
  };
}
export const retryJob: Thunk<{
  readonly id: string;
}> =
  ({ id }) =>
  async (dispatch, getState) => {
    const query = `
      mutation retryJob($id: ID!) {
        retryJob(id: $id) {
          id
          status
        }
      }
    `;

    return await callGraphQLApi<RetryJobResponse>({
      actionTypes: [
        Actions.NOOP_JOB_ACTION,
        Actions.RETRY_JOB_SUCCESS,
        Actions.RETRY_JOB_FAILED,
      ],
      query,
      variables: { id },
      dispatch,
      getState,
    });
  };

export interface GetMailboxesResponse {
  notificationMailboxes: {
    name: string;
    id: string;
    eventFilter: {
      eventNames: string[];
      eventType: string;
      applicationId: string;
    };
  }[];
}
export const getMailboxes: Thunk<void> = () => async (dispatch, getState) => {
  const query = `
    query notificationMailboxes {
      notificationMailboxes{
        name
        id
        eventFilter{
          eventNames
          eventType
          applicationId
        }
      }
    }
    `;

  return await callGraphQLApi<GetMailboxesResponse>({
    actionTypes: [
      Actions.FETCH_MAILBOX,
      Actions.FETCH_MAILBOX_SUCCESS,
      Actions.FETCH_MAILBOX_FAILURE,
    ],
    query,
    variables: {},
    dispatch,
    getState,
  });
};

export interface CreateMailboxAppResponse {
  notificationMailboxCreate: {
    id: string;
    name: string;
  };
}
export const createMailboxApp: Thunk<{ applicationId: string }> =
  ({ applicationId }) =>
  async (dispatch, getState) => {
    const query = `
      mutation createMailbox {
        notificationMailboxCreate(input:{
          name: "Mailbox Redact",
          eventFilter: {
            eventNames: ["redactNotificationEvent"],
            eventType: "redact",
            applicationId:"${applicationId}",
            delivery:{
              deliveryType:NotificationMailbox
            }
          },
          notificationTemplate:"{{message}}",
        }) {
          id
          name
        }
      }`;

    return await callGraphQLApi<CreateMailboxAppResponse>({
      actionTypes: [
        Actions.CREATE_MAILBOX_APP,
        Actions.CREATE_MAILBOX_APP_SUCCESS,
        Actions.CREATE_MAILBOX_APP_FAILURE,
      ],
      query,
      variables: {},
      dispatch,
      getState,
    });
  };

export interface CreateNotiAppResponse {
  notificationPost: {
    id: string;
  };
}
export const createNotiApp: Thunk<{
  message: string;
  title: string;
  mailboxId: string;
}> =
  ({ message, mailboxId, title }) =>
  async (dispatch, getState) => {
    const query = `
      mutation {
        notificationPost(
          input: {
            mailboxIds: ["${mailboxId}"],
            body: "${message}"
            contentType: "string"
            title: "${title}"
            flags: [unread, unseen],
          }
        ) {
          id
        }
      }`;

    return await callGraphQLApi<CreateNotiAppResponse>({
      actionTypes: [
        Actions.NOOP_JOB_ACTION,
        Actions.NOOP_JOB_ACTION,
        Actions.NOOP_JOB_ACTION,
      ],
      query,
      variables: {},
      getState,
      dispatch,
    });
  };
